import { __, sprintf } from "@wordpress/i18n";
import { useMemo, useState } from "@wordpress/element";
import { AJAX_ACTION, ENUM, LIMITS, NONCE, OPTIONS, USERS, AVAILABLE_ROLES } from "../../utils/constants";
import { Button } from "../../components/button";
import { ajax } from "../../config/axios";
import { toast } from "react-toastify";
import { css } from "@/utils/css";
import { SwitchBox } from "@/components/switch";
import { TextareaInput } from "@/components/textarea-input";
import { Select } from "@/components/select";
import { useUsageStats } from "@/hooks/use-usage-stats";
import { IS_PRO } from "@/utils/constants";
import { SimpleProAlert } from "@/components/pro-alert-box";
import { NumberInput } from "@/components/number-input";

export default function TopicResponseSection() {
    const [submitting, setSubmitting] = useState(false);
    const [selectedUser, setSelectedUser] = useState(OPTIONS.AI_AUTHOR_ID);
    const users = useMemo(
        () =>
            USERS?.map((user) => ({
                label: user.name,
                value: `${user.id}`,
            })),
        []
    );
    const [disclaimer, setDisclaimer] = useState(OPTIONS.AI_RESPONSE_NOTICE);
    const [responseEnabled, setResponseEnabled] = useState(OPTIONS.AI_RESPONSE_TO_TOPIC);
    const [replyResponseEnabled, setReplyResponseEnabled] = useState(OPTIONS.AI_RESPONSE_TO_REPLY);
    const [responseAsReply, setResponseAsReply] = useState(OPTIONS.AI_RESPONSE_AS_REPLY);
    const [replyMinWords, setReplyMinWords] = useState(OPTIONS.AI_REPLY_MIN_WORDS);
    const [excludedRoles, setExcludedRoles] = useState<string[]>(OPTIONS.EXCLUDED_ROLES);

    const { data: usageStats } = useUsageStats();

    // Show AI Author selector if either Response As Reply is enabled OR AI Response to Replies is enabled
    const showAiAuthor = (responseEnabled && responseAsReply) || replyResponseEnabled;

    const handleSubmit = async () => {
        setSubmitting(true);

        try {
            await ajax({
                nonce: NONCE.TOPIC_RESPONSE_PREFERENCES,
                action: AJAX_ACTION.TOPIC_RESPONSE_PREFERENCES,
                [ENUM.AI_RESPONSE_TO_TOPIC]: Number(responseEnabled),
                [ENUM.AI_RESPONSE_TO_REPLY]: Number(replyResponseEnabled),
                [ENUM.AI_RESPONSE_AS_REPLY]: Number(responseAsReply),
                [ENUM.AI_RESPONSE_NOTICE]: disclaimer,
                [ENUM.AI_AUTHOR_ID]: selectedUser,
                [ENUM.AI_REPLY_MIN_WORDS]: replyMinWords,
                [ENUM.AI_EXCLUDED_ROLES]: JSON.stringify(excludedRoles),
            });

            toast.success(__("Settings saved successfully", "antimanual"));
        } catch (e: any) {
            toast.error(e?.message || __("Something went wrong", "antimanual"));
        }

        setSubmitting(false);
    };

    return (
        <>
            <div className={css("p-8")}>
                {/* Usage Stats at the top */}
                {!IS_PRO && (
                    <div className={css("mb-6")}>
                        <SimpleProAlert>
                            {sprintf(__("Usage: %1$d / %2$d", "antimanual"), usageStats?.forum_answer?.count || 0, usageStats?.forum_answer?.limit || LIMITS.FORUM_ANSWER_MONTHLY)}
                        </SimpleProAlert>
                    </div>
                )}

                <div className={css("flex flex-wrap gap-8")}>
                    {/* Left Column: AI Response to Topics */}
                    <div className={css("max-w-xl")}>
                        <SwitchBox
                            className={css("mb-5")}
                            label={__("AI Response to Topics", "antimanual")}
                            help={__("If enabled, Antimanual will generate a response when a new topic is created.", "antimanual")}
                            checked={responseEnabled}
                            onChange={(value) => setResponseEnabled(value)}
                        />
                        <input type="hidden" name={ENUM.AI_RESPONSE_TO_TOPIC} value={Number(responseEnabled)} />

                        {/* Show Response As Reply only if AI Response to Topics is enabled */}
                        {responseEnabled && (
                            <>
                                <SwitchBox
                                    className={css("mb-5")}
                                    label={__("Response As Reply", "antimanual")}
                                    help={__("If enabled, The response will be included as a reply to the topic.", "antimanual")}
                                    checked={responseAsReply}
                                    onChange={(value) => setResponseAsReply(value)}
                                />
                                <input type="hidden" name={ENUM.AI_RESPONSE_AS_REPLY} value={Number(responseAsReply)} />
                            </>
                        )}
                    </div>

                    {/* Right Column: AI Response to Replies */}
                    <div className={css("max-w-xl")}>
                        <SwitchBox
                            className={css("mb-5")}
                            label={__("AI Response to Replies", "antimanual")}
                            help={__("If enabled, Antimanual will suggest an AI response when users submit replies to topics.", "antimanual")}
                            checked={replyResponseEnabled}
                            onChange={(value) => setReplyResponseEnabled(value)}
                        />
                        <input type="hidden" name={ENUM.AI_RESPONSE_TO_REPLY} value={Number(replyResponseEnabled)} />

                        {/* Show Minimum Reply Words only if AI Response to Replies is enabled */}
                        {replyResponseEnabled && (
                            <NumberInput
                                label={__("Minimum Reply Words", "antimanual")}
                                help={__(
                                    "Skip AI suggestion for replies shorter than this word count. Short replies like 'Thanks!' don't need AI assistance.",
                                    "antimanual"
                                )}
                                value={replyMinWords}
                                onChange={(value) => setReplyMinWords(value)}
                                min={1}
                                max={100}
                                className={css("mb-6")}
                            />
                        )}
                    </div>
                </div>

                {/* Shared Settings - shown when any AI response feature is enabled */}
                {(responseEnabled || replyResponseEnabled) && (
                    <div className={css("mt-6 pt-8 border-t border-gray-200 max-w-2xl")}>
                        {/* Show AI Author if Response As Reply is enabled OR AI Response to Replies is enabled */}
                        {showAiAuthor && (
                            <Select
                                label={__("AI Author", "antimanual")}
                                help={__(
                                    "Select the AI author account that will be used to post the AI-generated response. Only Administrator, Editor, and Author accounts are allowed to be selected as the AI Author.",
                                    "antimanual"
                                )}
                                value={selectedUser}
                                onChange={(value) => setSelectedUser(value)}
                                options={users || []}
                            />
                        )}

                        <TextareaInput
                            label={__("AI Response Disclaimer", "antimanual")}
                            help={__(
                                "This disclaimer will be shown above the AI-generated response to inform users that the following reply was generated by AI and may not always be fully accurate.",
                                "antimanual"
                            )}
                            value={disclaimer || ""}
                            onChange={(value) => setDisclaimer(value)}
                            className={css("block mt-6")}
                        />

                        <Select
                            label={__("Exclude Roles from AI Suggestions", "antimanual")}
                            help={__(
                                "Select user roles that should NOT see the AI suggestion modal when posting. The AI Author is automatically excluded. Use this to prevent support staff or administrators from seeing unnecessary AI popups.",
                                "antimanual"
                            )}
                            value={excludedRoles}
                            onChange={(value) => setExcludedRoles(value)}
                            options={AVAILABLE_ROLES}
                            isMulti
                            className={css("block mt-6")}
                        />
                    </div>
                )}

                <Button className={css("min-w-xs mt-8")} loading={submitting} type="button" onClick={handleSubmit}>
                    {__("Save Changes", "antimanual")}
                </Button>
            </div>
        </>
    );
}

